import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppContext } from "../../AppProvider";
import { useHandlerContext } from "./providers/Handler";
import { faArrowLeft, faLoader } from "../../utilities/fontawesomeIcons";
import { Input, Nav, NavItem, NavLink, TabPane, TabContent } from "reactstrap";
import { Link } from "react-router-dom";
import { Company } from "../../Models/Company";
import InputIcon from "../../Components/Inputs/InputIcon/InputIcon";
import { getCompanyPath } from "./utilities";
import { getFieldError } from "../../utilities/utilities";
import NoAvailableImage from "../../assets/images/no-image-available.png";
import classNames from "classnames";
import { AddressBookIndexProvider } from "../AddressBook/providers";
import AddressBooks from "../AddressBook/AddressBookIndex";

export const HandlerCompany = () => {
  const { dictionary, owner } = useAppContext();
  const { data, action, error, loader, edit, type, activeTab, setActiveTab } =
    useHandlerContext();

  const titleActions: Record<string, string> = {
    create: dictionary.words.new_sm,
    clone: dictionary.actions.duplicate,
    edit: dictionary.actions.edit,
    details: dictionary.actions.details,
  };

  const titleTypes = {
    CUSTOMER: dictionary.registries.customer,
    AFILIATE: dictionary.registries.affiliate,
    ORGANIZATION: dictionary.registries.organization,
    MASTER_ORGANIZATION: `Master ${dictionary.registries.organization}`,
  };

  const handleChange = (
    name: string,
    value: string | boolean | number | object | null,
    key: string
  ) => {
    edit(
      {
        ...data,
        [name]: value,
      } as Company,
      key as keyof Company
    );
  };

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <>
      <div className="row align-items-center mb-2">
        <div className="col">
          <h2 className="m-0">
            {titleActions[action]} {titleTypes[type]}
          </h2>
        </div>
      </div>
      <Nav tabs>
        <NavItem>
          <NavLink
            className={classNames(
              {
                active: activeTab === "1",
              },
              "cursor-pointer"
            )}
            onClick={() => {
              toggleTab("1");
            }}
          >
            {dictionary.words.general_informations}
          </NavLink>
        </NavItem>
        <NavItem hidden={!data?.id}>
          <NavLink
            className={classNames(
              { active: activeTab === "2" },
              "cursor-pointer"
            )}
            onClick={() => {
              toggleTab("2");
            }}
          >
            {dictionary.contacts.destinations_address_book}
          </NavLink>
        </NavItem>
        <NavItem hidden={!data?.id}>
          <NavLink
            className={classNames(
              { active: activeTab === "3" },
              "cursor-pointer"
            )}
            onClick={() => {
              toggleTab("3");
            }}
          >
            {dictionary.locations.addresses}
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeTab}>
        <TabPane tabId="1">
          {!loader ? (
            <>
              <div
                className="d-grid grid-column mt-1"
                style={{ gridTemplateColumns: `1fr 2fr` }}
              >
                <div className="box-container h-100">
                  <div
                    className="d-grid grid-rows justify-content-center h-100"
                    style={{ gridTemplateColumns: `1fr` }}
                  >
                    <div className="d-flex justify-content-center">
                      <div>
                        <img
                          style={{
                            maxWidth: "300px",
                            maxHeight: "300px",
                            width: "auto",
                            height: "auto",
                            objectFit: "contain",
                            display: "block",
                          }}
                          src={data.logoDarkUrl || NoAvailableImage}
                          alt={data.logoDarkUrl ? `${data.name} logo` : ""}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="label-2">
                        {dictionary.registries.logo_url}
                      </label>
                      <InputIcon
                        type="text"
                        value={data.logoDarkUrl || ""}
                        name="logoDarkUrl"
                        onChange={({ target }) => {
                          handleChange(
                            target.name,
                            target.value,
                            "logoDarkUrl"
                          );
                        }}
                        error={getFieldError(error, "logoDarkUrl")}
                      />
                    </div>
                  </div>
                </div>

                <div className="box-container h-100">
                  <div
                    className="d-grid grid-column mb-2"
                    style={{ gridTemplateColumns: `0.5fr` }}
                  >
                    <div>
                      <label className="label-2">
                        {dictionary.registries.company}
                      </label>
                      <h3>{owner?.name}</h3>
                    </div>
                  </div>

                  <Input
                    type="hidden"
                    name="type"
                    className="form-control"
                    value={type}
                  />

                  <div
                    className="d-grid grid-column mb-2"
                    style={{ gridTemplateColumns: `1fr 1fr` }}
                  >
                    <div>
                      <label className="label-2">
                        {dictionary.registries.company_name}
                      </label>
                      <InputIcon
                        type="text"
                        onChange={({ target }) => {
                          handleChange(target.name, target.value, "name");
                        }}
                        value={data.name || ""}
                        name="name"
                        error={getFieldError(error, "name")}
                      />
                    </div>
                    <div>
                      <label className="label-2">
                        {dictionary.registries.commercial_name}
                      </label>
                      <InputIcon
                        type="text"
                        onChange={({ target }) => {
                          handleChange(
                            target.name,
                            target.value,
                            "friendlyName"
                          );
                        }}
                        value={data.friendlyName || ""}
                        name="friendlyName"
                        error={getFieldError(error, "friendlyName")}
                      />
                    </div>
                  </div>
                  <div
                    className="d-grid grid-column mb-2"
                    style={{ gridTemplateColumns: `1fr 1fr` }}
                  >
                    <div>
                      <label className="label-2">
                        {dictionary.registries.vat_number}
                      </label>
                      <InputIcon
                        type="text"
                        name="vatNumber"
                        value={data.vatNumber}
                        onChange={({ target }) => {
                          handleChange(target.name, target.value, "vatNumber");
                        }}
                        error={getFieldError(error, "vatNumber")}
                      />
                    </div>
                    <div>
                      <label className="label-2">
                        {dictionary.references.external_reference}
                      </label>
                      <InputIcon
                        type="text"
                        value={data.externalReference || ""}
                        name="externalReference"
                        onChange={({ target }) => {
                          handleChange(
                            target.name,
                            target.value,
                            "externalReference"
                          );
                        }}
                        error={getFieldError(error, "externalReference")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center mt-2">
              <FontAwesomeIcon icon={faLoader} spin size="2x" />
            </div>
          )}
        </TabPane>

        <TabPane tabId="2" hidden={!data?.id}>
          {activeTab === "2" && (
            <div className="mt-1">
              <AddressBookIndexProvider
                myAddress={false}
                ownerId={data?.id}
                service="registry"
                handlerConfig={{
                  billing: { enabled: true },
                  registry: { saveEnabled: false, searchEnabled: false },
                }}
              >
                <AddressBooks />
              </AddressBookIndexProvider>
            </div>
          )}
        </TabPane>

        <TabPane tabId="3" hidden={!data?.id}>
          {activeTab === "3" && (
            <div className="mt-1">
              <AddressBookIndexProvider
                myAddress={true}
                ownerId={data?.id}
                service="registry"
                handlerConfig={{
                  billing: { enabled: false },
                  registry: { saveEnabled: false, searchEnabled: false },
                  defaultValue: {
                    name: data.name,
                    externalReference: data.externalReference,
                  },
                }}
              >
                <AddressBooks />
              </AddressBookIndexProvider>
            </div>
          )}
        </TabPane>
      </TabContent>
      <div className="row mt-3">
        <div className="col">
          <Link
            to={`/${getCompanyPath(type)}`}
            className="button-1 button-1-transparent"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> {dictionary.words.back}
          </Link>
        </div>
      </div>
    </>
  );
};
